import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IGroupFilterCriteria, Contact, ContactModel } from "common247";

export interface IGroupFilterComponentProps {
  criteria: IGroupFilterCriteria;
  onChange: (updated: IGroupFilterCriteria) => void;
  allContacts?: (Contact | ContactModel)[];
}

const MEMBER_COUNTS = [
  { count: 0, label: "Any Size" },
  { count: 1, label: "1+ Members" },
  { count: 3, label: "3+ Members" },
  { count: 5, label: "5+ Members" },
];

/**
 * Filter controls specific to Group entities.
 */
export function GroupFilter({
  criteria,
  onChange,
  allContacts = [],
}: IGroupFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const currentContactIds = criteria.contactIds || [];
  const minMemberCount = criteria.minMemberCount || 0;

  const handleContactToggle = (contactId: string): void => {
    const updated = currentContactIds.includes(contactId)
      ? currentContactIds.filter((id) => id !== contactId)
      : [...currentContactIds, contactId];
    onChange({ ...criteria, contactIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Min Members */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Group Size</ThemedText>
        <View style={styles.chipsWrap}>
          {MEMBER_COUNTS.map((item) => (
            <FilterChip
              key={item.count}
              label={item.label}
              selected={minMemberCount === item.count}
              onPress={() => onChange({ ...criteria, minMemberCount: item.count === 0 ? undefined : item.count })}
              compact
            />
          ))}
        </View>
      </View>

      {/* Contains Member Contacts */}
      {allContacts.length > 0 && (
        <View style={styles.section}>
          <ThemedText style={[styles.label, { color: textMuted }]}>Contains Member</ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
            {allContacts.map((c) => (
              <FilterChip
                key={c.id}
                label={c.displayName}
                icon="person-outline"
                selected={currentContactIds.includes(c.id || "")}
                onPress={() => c.id && handleContactToggle(c.id)}
                compact
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  horizScroll: {
    flexDirection: "row",
  },
});
